import { useEffect, useRef, useState } from "react";
import * as AsciinemaPlayer from "asciinema-player";
import "asciinema-player/dist/bundle/asciinema-player.css";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { ReplayControls } from "@/components/ReplayControls";

const POSITION_POLL_MS = 100;

export function SshPlayer({
  castFile,
  error,
}: {
  castFile: string | null;
  error: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<AsciinemaPlayer.Player | null>(null);
  const resumeAtRef = useRef(0);
  const wasPlayingRef = useRef(true);

  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    const container = containerRef.current;

    if (!castFile || !container) return;

    const player = AsciinemaPlayer.create({ data: castFile }, container, {
      fit: "both",
      controls: false,
      preload: true,
      speed,
      startAt: resumeAtRef.current,
    });

    playerRef.current = player;

    player.addEventListener("play", () => setPlaying(true));
    player.addEventListener("pause", () => setPlaying(false));
    player.addEventListener("ended", () => setPlaying(false));

    if (wasPlayingRef.current) void player.play();

    const timer = setInterval(() => {
      setPosition(player.getCurrentTime());

      const total = player.getDuration();

      if (total !== undefined) setDuration(total);
    }, POSITION_POLL_MS);

    return () => {
      clearInterval(timer);

      resumeAtRef.current = player.getCurrentTime();
      playerRef.current = null;

      player.dispose();
    };
  }, [castFile, speed]);

  if (error) return <ErrorState message={error} />;
  if (!castFile) return <LoadingState />;

  return (
    <div>
      <div ref={containerRef} className="h-[60vh]" />

      <ReplayControls
        position={position}
        duration={duration}
        playing={playing}
        speed={speed}
        onPlayPause={() => {
          const player = playerRef.current;

          if (!player) return;

          if (playing) {
            wasPlayingRef.current = false;
            void player.pause();

            return;
          }

          wasPlayingRef.current = true;
          void player.play();
        }}
        onSeek={(seconds) => {
          setPosition(seconds);
          void playerRef.current?.seek(seconds);
        }}
        onSpeedChange={setSpeed}
      />
    </div>
  );
}
